import RootState from '@/store/root_state';
import { ActionTree } from 'vuex';
import { StudyPlanState } from './state';
import { Kingslanding } from '@/services/kingslanding';
import { StudyPlanTemplateModel } from '@/models/kingslanding/studyPlanTemplateModel';
import * as dateHelper from '@/helpers/date';
import { WeekRange } from '@/helpers/date';
import { StudyPlanTemplateActivities } from '@/models/kingslanding/studyPlanTemplateActivities';
import { dexQuery } from '@/helpers/graphql';
import { getListInfoById } from '@/store/queries/dexQueries';
import { normalize } from 'normalizr';
import { list } from '@descomplica/dex-store/lib/normalizr_schema';
import SentryClient from '@/helpers/sentry';
import { AppointmentModel } from '@descomplica/dex-models';

interface GetStudentCourseStudyPlanInput {
  courseId: number;
  moduleId: number;
  disciplineIds: number[];
}

const actions: ActionTree<StudyPlanState, RootState> = {
  async GET_STUDENT_COURSE_STUDY_PLAN(
    { commit, dispatch, getters },
    { courseId, moduleId, disciplineIds }: GetStudentCourseStudyPlanInput,
  ): Promise<void> {
    const { getStudyPlan, studyPlanByCourseModule } = getters;

    if (getStudyPlan && courseId === getStudyPlan.courseId && moduleId === getStudyPlan.moduleId) {
      return;
    }

    const studyPlan: StudyPlanTemplateModel = studyPlanByCourseModule(courseId, moduleId);

    if (studyPlan) {
      commit('SET_CURRENT_STUDY_PLAN_DISPLAYED', studyPlan.id);
      dispatch('PREPARE_STUDY_PLAN', disciplineIds);

      return;
    }

    if (!studyPlan) {
      commit('SET_IS_LOADING_COURSE_STUDY_PLAN', true);
      dispatch('CLEAR_SELECTED_STUDY_PLAN');

      try {
        const studyPlanTemplate: StudyPlanTemplateModel =
          await Kingslanding.getStudentCourseModuleStudyPlan({
            courseId,
            moduleId,
            disciplineIds,
          });

        if (
          studyPlanTemplate &&
          studyPlanTemplate.activities &&
          studyPlanTemplate.activities.length
        ) {
          commit('SET_STUDY_PLANS', studyPlanTemplate);
          commit('SET_CURRENT_STUDY_PLAN_DISPLAYED', studyPlanTemplate.id);

          dispatch('PREPARE_STUDY_PLAN', disciplineIds);
        }
      } catch (error) {
        SentryClient.captureError(error);
      } finally {
        commit('SET_IS_LOADING_COURSE_STUDY_PLAN', false);
      }
    }
  },
  PREPARE_STUDY_PLAN({ commit, dispatch, getters }, disciplineIds: number[]): void {
    const studyPlanTemplate: StudyPlanTemplateModel = getters.getStudyPlan;
    const appointments: AppointmentModel[] = getters.appointmentsByClassroomIds(disciplineIds);
    commit('SET_STUDY_PLAN_APPOINTMENTS', { id: studyPlanTemplate.id, appointments });

    const today = new Date();
    const weekRange: WeekRange = dateHelper.getWeekRangeByDate(today);
    const { startDate, endDate } = weekRange;
    const weekActivities: StudyPlanTemplateActivities[] = studyPlanTemplate.activities.filter(
      (activity: StudyPlanTemplateActivities) =>
        new Date(activity.startDateTime) >= new Date(startDate) &&
        new Date(activity.startDateTime) <= new Date(endDate),
    );

    if (weekActivities && weekActivities.length) {
      const weekActivitiesIds: number[] = weekActivities.map(
        (weekActivity: StudyPlanTemplateActivities) => weekActivity.pedagogicalActivityId,
      );
      const currentWeek: number = weekActivities[0].week;

      commit('SET_CURRENT_WEEK', currentWeek);
      commit('SET_LAST_PREVIOUS_WEEK_LOADED', currentWeek);
      commit('SET_LAST_NEXT_WEEK_LOADED', currentWeek);

      dispatch('FETCH_STUDY_PLAN_WEEK_ACTIVITIES', weekActivitiesIds);
    }
  },
  LOAD_NEXT_WEEK({ state, commit, dispatch, getters }): void {
    commit('INCREASE_NEXT_WEEK_LOADED');

    const { activitiesIdsByWeek } = getters;

    const { lastNextWeekLoaded } = state;
    const activitiesToLoad: number[] = activitiesIdsByWeek(lastNextWeekLoaded);

    dispatch('FETCH_STUDY_PLAN_WEEK_ACTIVITIES', activitiesToLoad);
  },
  LOAD_PREVIOUS_WEEK({ state, commit, dispatch, getters }): void {
    commit('DECREASE_PREVIOUS_WEEK_LOADED');

    const { activitiesIdsByWeek } = getters;

    const { lastPreviousWeekLoaded } = state;
    const activitiesToLoad: number[] = activitiesIdsByWeek(lastPreviousWeekLoaded);

    dispatch('FETCH_STUDY_PLAN_WEEK_ACTIVITIES', activitiesToLoad);
  },
  async FETCH_STUDY_PLAN_WEEK_ACTIVITIES(
    { getters, dispatch, commit },
    activitiesIds: number[],
  ): Promise<void> {
    const { listById } = getters;

    dispatch('PULL_LISTS_PROGRESS', {
      listIds: activitiesIds,
    });

    activitiesIds.map(async (activityId: number) => {
      const hasActivityData = listById(activityId);

      if (!hasActivityData) {
        const listInfoResponse = await dexQuery(getListInfoById, {
          listId: activityId,
        });

        const normalizedResponse = normalize(listInfoResponse.data.listById, list);

        commit('SET_LISTS', normalizedResponse.entities);
        commit('SET_LIST_ITEMS', normalizedResponse.entities);
      }

      commit('SET_ACTIVITY_LOADED', activityId);
    });
  },
  async CLEAR_SELECTED_STUDY_PLAN({ commit }): Promise<void> {
    commit('SET_CURRENT_STUDY_PLAN_DISPLAYED', -1);
    commit('SET_CURRENT_WEEK', -1);
    commit('SET_LAST_PREVIOUS_WEEK_LOADED', -1);
    commit('SET_LAST_NEXT_WEEK_LOADED', -1);
  },
};

export default actions;
